/**
 * Public landing for a LABEL archetype tenant (Disorder Group onboarding
 * 2026-10-15). Labels don't operate a public venue site , no tickets,
 * no gigs, no memberships , so public visitors see a minimal splash
 * with a single CTA into the admin dashboard.
 *
 * Design language from the Disorder Records brand book (Aether 2024):
 *   - Pure monochrome: black field, white marks
 *   - "Dynamic bar and line" treatment, horizontal rules frame the
 *     wordmark; corner glitch bars echo the brand's "glitch expressions"
 *   - Typography: Neue Haas Grotesk when the venue has it loaded via
 *     appConfig.customFontUrl; falls back to a Helvetica stack
 *   - Everything uppercase + letter-spaced for the typographic voice
 *
 * The wordmark comes from `venue.imageUrl` so the component is
 * white-label: any future LABEL tenant uploads their logo via
 * /admin/branding and the landing picks it up. Fallback is the venue
 * name set in Grotesk-bold between the rules.
 */
import { Link } from "@tanstack/react-router";
import { useVenue } from "../lib/venue";

export function LabelLandingPage() {
  const { data } = useVenue();
  const venue = data?.venue;
  const name = venue?.appName || venue?.name || "";
  const wordmarkUrl = venue?.imageUrl ?? null;
  const fontFamily = (venue?.fontFamily ?? "").trim();
  const customFontUrl = (venue?.appConfig as { customFontUrl?: string } | undefined)?.customFontUrl ?? null;

  // Neue Haas Grotesk is commercially licensed; honour the venue's own
  // customFontUrl when set (uploaded via admin), otherwise use a
  // Helvetica stack that gets as close as possible on every OS.
  const resolvedFont = fontFamily
    || "'Neue Haas Grotesk Display Pro', 'Neue Haas Grotesk', 'Helvetica Neue', Helvetica, Arial, sans-serif";

  return (
    <>
      {customFontUrl && (
        <style>{`
          @font-face {
            font-family: 'Neue Haas Grotesk Display Pro';
            src: url('${encodeURI(customFontUrl)}') format('opentype'),
                 url('${encodeURI(customFontUrl)}') format('truetype');
            font-weight: 500;
            font-display: swap;
          }
        `}</style>
      )}
      <div
        className="relative flex min-h-[100dvh] w-full flex-col items-center justify-center bg-black px-6 py-12 text-white"
        style={{ fontFamily: resolvedFont }}
      >
        {/* Corner glitch bars , the "dynamic bar and line" treatment
            from the Disorder brand book. Decorative; hidden on tiny
            screens so the centre composition breathes on mobile. */}
        <div className="pointer-events-none absolute left-0 top-10 hidden h-6 w-[22vw] bg-white sm:block" />
        <div className="pointer-events-none absolute right-0 top-24 hidden h-[2px] w-[18vw] bg-white sm:block" />
        <div className="pointer-events-none absolute left-0 bottom-24 hidden h-[2px] w-[18vw] bg-white sm:block" />
        <div className="pointer-events-none absolute right-0 bottom-10 hidden h-6 w-[22vw] bg-white sm:block" />

        {/* Centre composition: horizontal rules above + below the mark,
            matching the Disorder lockup from the brand book. */}
        <div className="flex w-full max-w-[720px] flex-col items-center">
          <div className="h-[2px] w-full bg-white" />
          <div className="flex min-h-[160px] w-full items-center justify-center px-6 py-10 sm:min-h-[220px] sm:py-14">
            {wordmarkUrl ? (
              <img
                src={wordmarkUrl}
                alt={name}
                className="h-16 w-auto max-w-[min(80vw,560px)] object-contain sm:h-24"
              />
            ) : (
              <span
                className="text-center text-3xl font-bold uppercase tracking-[0.25em] text-white sm:text-5xl"
                style={{ fontFamily: resolvedFont }}
              >
                {name || "Label"}
              </span>
            )}
          </div>
          <div className="h-[2px] w-full bg-white" />
        </div>

        <p className="mt-10 max-w-md text-center text-[13px] uppercase tracking-[0.18em] text-white/60">
          Private workspace
        </p>
        <p className="mt-3 max-w-md text-center text-sm text-white/80">
          Head to the dashboard to manage promo campaigns, DJ feedback, and reports.
        </p>

        <div className="mt-9 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <Link
            to="/admin"
            className="inline-flex items-center justify-center border border-white bg-white px-8 py-3 text-[12px] font-semibold uppercase tracking-[0.22em] text-black transition hover:bg-white/90"
            style={{ fontFamily: resolvedFont }}
          >
            Open dashboard →
          </Link>
          <Link
            to="/sign-in"
            className="inline-flex items-center justify-center border border-white/70 bg-transparent px-8 py-3 text-[12px] font-semibold uppercase tracking-[0.22em] text-white transition hover:border-white hover:bg-white/5"
            style={{ fontFamily: resolvedFont }}
          >
            Admin sign in
          </Link>
        </div>

        {/* "Powered by Assembly" with the bar motif. */}
        <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-3">
          <div className="h-[1px] w-10 bg-white/30" />
          <span className="text-[10px] uppercase tracking-[0.32em] text-white/40">
            Powered by Assembly
          </span>
          <div className="h-[1px] w-10 bg-white/30" />
        </div>
      </div>
    </>
  );
}
